import React, { useEffect, useState } from 'react';
import { DeployTest, DeployTestActionItem } from '../../types';
import { DeployProgram } from '../../config/deployProgram';
import { hasOutstandingLinearItems } from './deployTestUtils';
import DeployTestActionItems, { ActionItemCreatePreset } from './DeployTestActionItems';

interface DeployTestTicketTabsProps {
  test: DeployTest;
  program: DeployProgram;
  onActionItemsChange: (testId: string, actionItems: DeployTestActionItem[]) => void;
  onTodoItemsChange: (testId: string, todoItems: DeployTestActionItem[]) => void;
  createPreset?: (ActionItemCreatePreset & { testId?: string }) | null;
  fixTestingOptions?: DeployTest[];
}

const DeployTestTicketTabs: React.FC<DeployTestTicketTabsProps> = ({
  test,
  program,
  onActionItemsChange,
  onTodoItemsChange,
  createPreset = null,
  fixTestingOptions = [],
}) => {
  const planning = test.status === 'planning';
  const [tab, setTab] = useState<'todos' | 'actions'>(planning ? 'todos' : 'actions');

  useEffect(() => {
    setTab(test.status === 'planning' ? 'todos' : 'actions');
  }, [test.status]);

  useEffect(() => {
    if (createPreset) setTab('actions');
  }, [createPreset?.nonce]);

  const outstandingTodos = (test.todoItems || []).filter((item) => hasOutstandingLinearItems([item])).length;
  const outstandingActions = (test.actionItems || []).filter((item) => hasOutstandingLinearItems([item])).length;

  return (
    <div className={`deploy-test-ticket-tabs is-${tab === 'todos' ? 'todo' : 'action'}`}>
      <div className="deploy-test-ticket-tablist" role="tablist" aria-label="TODOs and Action Items">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'todos'}
          className={`deploy-test-ticket-tab is-todo${tab === 'todos' ? ' is-active' : ''}`}
          onClick={() => setTab('todos')}
        >
          TODOs
          {outstandingTodos > 0 && (
            <span className="deploy-test-ticket-tab-count">{outstandingTodos}</span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'actions'}
          className={`deploy-test-ticket-tab is-action${tab === 'actions' ? ' is-active' : ''}`}
          onClick={() => setTab('actions')}
        >
          Action Items
          {outstandingActions > 0 && (
            <span className="deploy-test-ticket-tab-count">{outstandingActions}</span>
          )}
        </button>
      </div>

      {tab === 'todos' ? (
        <DeployTestActionItems
          test={test}
          program={program}
          variant="todo"
          canCreate={planning}
          hideHeading
          onActionItemsChange={onTodoItemsChange}
          fixTestingOptions={fixTestingOptions}
        />
      ) : (
        <DeployTestActionItems
          test={test}
          program={program}
          variant="action"
          canCreate={!planning}
          hideHeading
          onActionItemsChange={onActionItemsChange}
          createPreset={createPreset}
          fixTestingOptions={fixTestingOptions}
        />
      )}
    </div>
  );
};

export default DeployTestTicketTabs;
